import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
// TODO: BACKEND - Import fetchWithToken when ready to connect backend
// import { fetchWithToken } from "../../../authentication/AuthProvider";
// import config from "../../../config";

// ===== BACKEND API HOOK =====
const useLEEDData = (buildingId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!buildingId) {
      setLoading(false);
      return;
    }

    // TODO: BACKEND - Replace mock data with actual API call
    // ========================================
    // BACKEND ENDPOINT NEEDED:
    // URL: /api/v1/buildings/{buildingId}/leed
    // METHOD: GET or POST
    // REQUEST BODY (if POST):
    // {
    //   "building_id": buildingId
    // }
    // 
    // RESPONSE STRUCTURE:
    // {
    //   "totalPoints": 72,
    //   "maxTotalPoints": 110,
    //   "categories": [
    //     {
    //       "id": "sustainable-sites",
    //       "name": "Sustainable Sites",
    //       "points": 8,
    //       "maxPoints": 26,
    //       "credits": [...]
    //     },
    //     ...
    //   ],
    //   "optimizationOpportunities": [...]
    // }
    // 
    // ACTUAL API CALL (uncomment when backend is ready):
    // ========================================
    /*
    const url = config.building.get_leed_data; // Add this to config.js
    fetchWithToken(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ building_id: buildingId })
    })
      .then((res) => res.json())
      .then((data) => {
        setData(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching LEED data:", error);
        setLoading(false);
      });
    */

    // MOCK DATA (remove when backend is connected)
    setTimeout(() => {
      setData({
        totalPoints: 72,
        maxTotalPoints: 110,
        categories: [
          {
            id: "sustainable-sites",
            name: "Sustainable Sites",
            points: 8,
            maxPoints: 26,
            icon: "🌍",
            credits: [
              { name: "Site Selection", points: 1, earned: 1 },
              { name: "Development Density", points: 5, earned: 5 },
              { name: "Brownfield Redevelopment", points: 1, earned: 0 },
              { name: "Alternative Transportation", points: 15, earned: 6 },
              { name: "Site Development", points: 2, earned: 2 },
              { name: "Stormwater Design", points: 2, earned: 0 },
            ],
          },
          {
            id: "water-efficiency",
            name: "Water Efficiency",
            points: 6,
            maxPoints: 10,
            icon: "💧",
            credits: [
              { name: "Water Efficient Landscaping", points: 4, earned: 2 },
              { name: "Innovative Wastewater Technologies", points: 2, earned: 2 },
              { name: "Water Use Reduction", points: 4, earned: 2 },
            ],
          },
          {
            id: "energy-atmosphere",
            name: "Energy & Atmosphere",
            points: 21,
            maxPoints: 35,
            icon: "⚡",
            credits: [
              { name: "Fundamental Commissioning", points: 1, earned: 1 },
              { name: "Minimum Energy Performance", points: 1, earned: 1 },
              { name: "Optimize Energy Performance", points: 19, earned: 15 },
            ],
          },
          {
            id: "materials-resources",
            name: "Materials & Resources",
            points: 12,
            maxPoints: 14,
            icon: "♻️",
            credits: [
              { name: "Storage & Collection of Recyclables", points: 1, earned: 1 },
              { name: "Construction Waste Management", points: 2, earned: 2 },
              { name: "Recycled Content", points: 2, earned: 2 },
            ],
          },
          {
            id: "indoor-quality",
            name: "Indoor Environmental Quality",
            points: 13,
            maxPoints: 15,
            icon: "🏠",
            credits: [
              { name: "Minimum IAQ Performance", points: 1, earned: 1 },
              { name: "Environmental Tobacco Smoke Control", points: 1, earned: 1 },
              { name: "Low-Emitting Materials", points: 4, earned: 4 },
            ],
          },
        ],
        optimizationOpportunities: [
          {
            category: "Energy & Atmosphere",
            opportunity: "Add solar panels for renewable energy",
            potentialPoints: 7,
            estimatedCost: "$85,000",
            paybackYears: 8.5,
            priority: "high",
          },
          {
            category: "Materials & Resources",
            opportunity: "Increase building reuse percentage",
            potentialPoints: 3,
            estimatedCost: "$25,000",
            paybackYears: "N/A",
            priority: "medium",
          },
        ],
      });
      setLoading(false);
    }, 500);
  }, [buildingId]);

  return { data, loading };
};

// ===== COMPONENT =====
const LEEDTab = () => {
  // ✅ ALWAYS CALL HOOKS FIRST (before any conditionals or returns)
  const { t } = useTranslation();
  const { selectedBuildings } = useContextProvider();
  const [activeTab, setActiveTab] = useState("categories");
  
  // Get building ID (or null if no building selected)
  const buildingId = selectedBuildings && selectedBuildings.length === 1 ? selectedBuildings[0].id : null;
  const selectedBuilding = selectedBuildings && selectedBuildings.length === 1 ? selectedBuildings[0] : null;
  
  // ✅ Call data hook (always, regardless of conditions)
  const { data, loading } = useLEEDData(buildingId);

  // ✅ NOW we can do conditional rendering (AFTER all hooks are called)
  if (!selectedBuilding) {
    return (
      <div style={{ padding: "20px", color: "#666" }}>
        {t("Please select a building from the dashboard to view LEED analysis.")}
      </div>
    );
  }

  if (loading) return <div style={{ padding: "20px", color: "#666" }}>{t("Loading LEED data...")}</div>;
  if (!data) return <div style={{ padding: "20px", color: "#666" }}>{t("No LEED data available.")}</div>;

  const totalPoints = data.totalPoints;
  const maxPoints = data.maxTotalPoints;
  const percentage = ((totalPoints / maxPoints) * 100).toFixed(1);

  const getCertLevel = (pts) => {
    if (pts >= 80) return { level: t("Platinum"), color: "#7c3aed" };
    if (pts >= 60) return { level: t("Gold"), color: "#ca8a04" };
    if (pts >= 50) return { level: t("Silver"), color: "#6b7280" };
    if (pts >= 40) return { level: t("Certified"), color: "#10b981" };
    return { level: t("Not Certified"), color: "#dc2626" };
  };

  const currentCert = getCertLevel(totalPoints);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* HEADER */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <h2 style={{ fontSize: "24px", fontWeight: "bold", margin: 0, color: "#1a1a1a" }}>
            {t("LEED Analysis")} - {selectedBuilding.name}
          </h2>
          <div style={{ fontSize: "13px", fontWeight: 600, color: currentCert.color }}>
            {t("Current:")} {currentCert.level}
          </div>
        </div>
        <p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
          {t("Leadership in Energy and Environmental Design certification tracking")}
        </p>
      </div>

      {/* METRIC CARDS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
        <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "20px" }}>
          <p style={{ fontSize: "12px", color: "#999", margin: "0 0 8px 0", fontWeight: 500, textTransform: "uppercase" }}>
            {t("Current Score")}
          </p>
          <p style={{ fontSize: "32px", fontWeight: "bold", color: currentCert.color, margin: "0 0 4px 0" }}>
            {totalPoints}
          </p>
          <p style={{ fontSize: "13px", color: "#666", margin: 0 }}>
            {t("out of")} {maxPoints} {t("points")} ({percentage}%)
          </p>
        </div>

        <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "20px" }}>
          <p style={{ fontSize: "12px", color: "#999", margin: "0 0 8px 0", fontWeight: 500, textTransform: "uppercase" }}>
            {t("Potential Score")}
          </p>
          <p style={{ fontSize: "32px", fontWeight: "bold", color: "#10b981", margin: "0 0 4px 0" }}>
            {totalPoints + data.optimizationOpportunities.reduce((sum, o) => sum + o.potentialPoints, 0)}
          </p>
          <p style={{ fontSize: "13px", color: "#666", margin: 0 }}>
            +{data.optimizationOpportunities.reduce((sum, o) => sum + o.potentialPoints, 0)} {t("possible points")}
          </p>
        </div>

        <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "20px" }}>
          <p style={{ fontSize: "12px", color: "#999", margin: "0 0 8px 0", fontWeight: 500, textTransform: "uppercase" }}>
            {t("Next Milestone")}
          </p>
          <p style={{ fontSize: "32px", fontWeight: "bold", color: "#f59e0b", margin: "0 0 4px 0" }}>
            {t("Platinum")}
          </p>
          <p style={{ fontSize: "13px", color: "#666", margin: 0 }}>
            {t("Need")} {80 - totalPoints} {t("more points")}
          </p>
        </div>
      </div>

      {/* TABS */}
      <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden" }}>
        {/* Tab Navigation */}
        <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #e0e0e0" }}>
          {[
            { id: "categories", label: "Credit Categories" },
            { id: "optimization", label: "Optimization" },
          ].map((tabItem) => (
            <button
              key={tabItem.id}
              onClick={() => setActiveTab(tabItem.id)}
              style={{
                flex: 1,
                padding: "14px 16px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: activeTab === tabItem.id ? 600 : 500,
                color: activeTab === tabItem.id ? "#0ea5e9" : "#999",
                borderBottom: activeTab === tabItem.id ? "3px solid #0ea5e9" : "none",
                transition: "all 0.2s ease",
              }}
            >
              {t(tabItem.label)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ padding: "24px" }}>
          {activeTab === "categories" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {data.categories.map((cat) => (
                <div key={cat.id} style={{ background: "#fafbfc", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "20px", overflow: "hidden" }}>
                  {/* Category Header */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", paddingBottom: "12px", borderBottom: "1px solid #e0e0e0" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: 600, margin: 0, color: "#1a1a1a" }}>
                      {t(cat.name)}
                    </h3>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "#0ea5e9" }}>
                      {cat.points} / {cat.maxPoints} {t("points")}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{
                      width: "100%",
                      height: "6px",
                      background: "#e0e0e0",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}>
                      <div style={{
                        width: `${(cat.points / cat.maxPoints) * 100}%`,
                        height: "100%",
                        background: "#10b981",
                        transition: "width 0.3s ease",
                      }} />
                    </div>
                  </div>

                  {/* Credits List */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
                    {cat.credits.map((cred, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "12px 0",
                          borderBottom: idx < cat.credits.length - 1 ? "1px solid #e0e0e0" : "none",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                          <div style={{
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: cred.earned === cred.points ? "#d1fae5" : cred.earned > 0 ? "#fef3c7" : "#f3f4f6",
                            color: cred.earned === cred.points ? "#10b981" : cred.earned > 0 ? "#f59e0b" : "#999",
                            fontSize: "12px",
                            fontWeight: 600,
                          }}>
                            {cred.earned === cred.points ? "✓" : cred.earned > 0 ? "◐" : "○"}
                          </div>
                          <span style={{ fontSize: "13px", color: "#333", fontWeight: 500 }}>{t(cred.name)}</span>
                        </div>

                        <span style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: cred.earned === cred.points ? "#10b981" : cred.earned > 0 ? "#f59e0b" : "#999",
                          minWidth: "50px",
                          textAlign: "right",
                        }}>
                          {cred.earned} / {cred.points}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "optimization" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {data.optimizationOpportunities.map((opp, idx) => (
                <div key={idx} style={{ background: "#fafbfc", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: "14px", fontWeight: 600, margin: "0 0 4px 0", color: "#1a1a1a" }}>
                        {t(opp.opportunity)}
                      </h4>
                      <p style={{ fontSize: "12px", color: "#666", margin: 0 }}>
                        {t(opp.category)}
                      </p>
                    </div>
                    <div
                      style={{
                        background: opp.priority === "high" ? "#fee2e2" : "#fef3c7",
                        color: opp.priority === "high" ? "#dc2626" : "#d97706",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "capitalize",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t(opp.priority)} {t("priority")}
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px", fontSize: "12px" }}>
                    <div>
                      <span style={{ color: "#999", fontSize: "11px" }}>{t("Points:")} </span>
                      <span style={{ fontWeight: 600, color: "#10b981", display: "block" }}>+{opp.potentialPoints}</span>
                    </div>
                    <div>
                      <span style={{ color: "#999", fontSize: "11px" }}>{t("Cost:")} </span>
                      <span style={{ fontWeight: 600, color: "#333", display: "block" }}>{opp.estimatedCost}</span>
                    </div>
                    <div>
                      <span style={{ color: "#999", fontSize: "11px" }}>{t("Payback:")} </span>
                      <span style={{ fontWeight: 600, color: "#333", display: "block" }}>{opp.paybackYears} {t("yrs")}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LEEDTab;
